import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { useT } from '../i18n';
import { Checkbox } from './Checkbox';
import { IconButton } from './IconButton';
import { Skeleton } from './Skeleton';
import { cx } from './cx';
import s from './Table.module.css';

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Valeur utilisée pour le tri ; absente => colonne non triable. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'start' | 'end' | 'center';
  width?: string | number;
  /** Masque la colonne sous cette largeur d'écran (classe utilitaire). */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Libellé accessible du tableau (obligatoire). */
  'aria-label': string;
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelectedChange?: (ids: Set<string>) => void;
  /** Tri contrôlé ; sinon géré en interne. */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Pagination côté client (taille de page). Absent => pas de pagination. */
  pageSize?: number;
  /** Virtualisation simple : hauteur du conteneur et hauteur fixe d'une ligne. */
  virtualize?: { height: number; rowHeight: number; overscan?: number };
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: ReactNode;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function compare(a: string | number | null | undefined, b: string | number | null | undefined) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return collator.compare(String(a), String(b));
}

export function Table<T>({
  columns,
  rows,
  getRowId,
  selectable,
  selected,
  onSelectedChange,
  sort: sortProp,
  onSortChange,
  pageSize,
  virtualize,
  onRowClick,
  loading,
  empty,
  ...rest
}: TableProps<T>) {
  const { t, formatNumber } = useT();
  const [sortInternal, setSortInternal] = useState<SortState | null>(null);
  const sort = sortProp !== undefined ? sortProp : sortInternal;
  const [page, setPage] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col?.sortValue) return rows;
    const sv = col.sortValue;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((x, y) => dir * compare(sv(x), sv(y)));
  }, [rows, sort, columns]);

  const pageCount = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount - 1);
  // Si les données rétrécissent (filtre), on revient dans la plage valide
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const visibleRows = pageSize ? sorted.slice(safePage * pageSize, (safePage + 1) * pageSize) : sorted;

  // Fenêtrage : on ne rend que les lignes visibles + marge
  let windowed = visibleRows;
  let padTop = 0;
  let padBottom = 0;
  if (virtualize) {
    const { height, rowHeight, overscan = 6 } = virtualize;
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const end = Math.min(visibleRows.length, Math.ceil((scrollTop + height) / rowHeight) + overscan);
    windowed = visibleRows.slice(start, end);
    padTop = start * rowHeight;
    padBottom = (visibleRows.length - end) * rowHeight;
  }

  const toggleSort = (col: Column<T>) => {
    if (!col.sortValue) return;
    let next: SortState | null;
    if (sort?.columnId !== col.id) next = { columnId: col.id, direction: 'asc' };
    else if (sort.direction === 'asc') next = { columnId: col.id, direction: 'desc' };
    else next = null;
    if (sortProp === undefined) setSortInternal(next);
    onSortChange?.(next);
  };

  const selectedSet = selected ?? new Set<string>();
  const pageIds = visibleRows.map(getRowId);
  const selectedOnPage = pageIds.filter((id) => selectedSet.has(id)).length;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;

  const toggleAll = () => {
    const next = new Set(selectedSet);
    if (allSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    onSelectedChange?.(next);
  };
  const toggleRow = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange?.(next);
  };

  const colSpan = columns.length + (selectable ? 1 : 0);
  const alignClass = (a?: string) => (a === 'end' ? s.end : a === 'center' ? s.center : undefined);

  return (
    <div className={s.wrap}>
      <div
        ref={scroller}
        className={cx(s.scroll, virtualize && s.virtual)}
        style={virtualize ? { maxHeight: virtualize.height } : undefined}
        onScroll={virtualize ? (e) => setScrollTop(e.currentTarget.scrollTop) : undefined}
        tabIndex={virtualize ? 0 : undefined}
        role={virtualize ? 'region' : undefined}
        aria-label={virtualize ? rest['aria-label'] : undefined}
      >
        <table className={s.table} aria-label={rest['aria-label']} aria-rowcount={sorted.length}>
          <thead>
            <tr>
              {selectable && (
                <th className={cx(s.th, s.check)} scope="col">
                  <Checkbox
                    aria-label={t('ui.table.selectAll')}
                    checked={allSelected}
                    indeterminate={selectedOnPage > 0 && !allSelected}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {columns.map((c) => {
                const active = sort?.columnId === c.id;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    className={cx(s.th, alignClass(c.align), c.hideOnMobile && s.hideMobile)}
                    style={{ width: c.width }}
                    aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : c.sortValue ? 'none' : undefined}
                  >
                    {c.sortValue ? (
                      <button type="button" className={s.sortBtn} onClick={() => toggleSort(c)}>
                        {c.header}
                        {active ? sort!.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} /> : <ChevronsUpDown size={14} className={s.muted} />}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 5 }, (_, i) => (
                <tr key={`sk-${i}`}>
                  {Array.from({ length: colSpan }, (_, j) => (
                    <td key={j} className={s.td}>
                      <Skeleton height={14} width={j === 0 ? 120 : '70%'} />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading && visibleRows.length === 0 && (
              <tr>
                <td colSpan={colSpan} className={s.emptyCell}>
                  {empty ?? t('ui.table.empty')}
                </td>
              </tr>
            )}
            {!loading && padTop > 0 && (
              <tr aria-hidden="true" style={{ height: padTop }}>
                <td colSpan={colSpan} />
              </tr>
            )}
            {!loading &&
              windowed.map((row) => {
                const id = getRowId(row);
                const isSel = selectedSet.has(id);
                return (
                  <tr
                    key={id}
                    className={cx(s.tr, isSel && s.selected, onRowClick && s.clickable)}
                    style={virtualize ? { height: virtualize.rowHeight } : undefined}
                    aria-selected={selectable ? isSel : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selectable && (
                      <td className={cx(s.td, s.check)} onClick={(e) => e.stopPropagation()}>
                        <Checkbox aria-label={t('ui.table.selectRow')} checked={isSel} onChange={() => toggleRow(id)} />
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.id} className={cx(s.td, alignClass(c.align), c.hideOnMobile && s.hideMobile)}>
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            {!loading && padBottom > 0 && (
              <tr aria-hidden="true" style={{ height: padBottom }}>
                <td colSpan={colSpan} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(pageSize || selectable) && (
        <div className={s.footer}>
          <span className={s.footText} aria-live="polite">
            {selectable && selectedSet.size > 0
              ? t('ui.table.selected', { count: formatNumber(selectedSet.size) })
              : t('ui.table.total', { count: formatNumber(sorted.length) })}
          </span>
          {pageSize && pageCount > 1 && (
            <nav className={s.pager} aria-label={t('ui.table.pagination')}>
              <IconButton
                label={t('ui.table.prev')}
                size="sm"
                variant="secondary"
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
                icon={<ChevronLeft size={16} className="flip-rtl" />}
              />
              <span className={s.footText}>{t('ui.table.page', { page: safePage + 1, count: pageCount })}</span>
              <IconButton
                label={t('ui.table.next')}
                size="sm"
                variant="secondary"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
                icon={<ChevronRight size={16} className="flip-rtl" />}
              />
            </nav>
          )}
        </div>
      )}
    </div>
  );
}
